// Estado de accesibilidad + reglas CSS que transforman la PÁGINA del cliente.
// Estas reglas viven en un <style> de document.head (no en el Shadow DOM) porque
// afectan al sitio. Todo se activa con atributos data-a11y-* en <html> y variables
// CSS, para que funcione en cualquier sitio sin conocer su marcado.

export type ColorMode =
  | "none"
  | "mono"
  | "darkhc"
  | "brighthc"
  | "lowsat"
  | "highsat"
  | "contrast";

export type CursorMode = "none" | "white" | "black";

import type { Lang } from "./i18n";

export type Profile =
  | ""
  | "motor"
  | "blind"
  | "colorblind"
  | "dyslexia"
  | "lowvision"
  | "cognitive"
  | "epilepsy"
  | "adhd";

export interface A11yState {
  // Contenido
  fontSize: number; // 0..8  (factor 1 + n*0.1)
  lineSp: number; // 0..4
  wordSp: number; // 0..4
  letterSp: number; // 0..4
  readableFont: boolean;
  links: boolean;
  imgDesc: boolean;
  blinks: boolean;
  muteMedia: boolean;
  magnifier: boolean;
  textMag: boolean;
  readFocus: boolean;
  readGuide: boolean;
  cursor: CursorMode;
  // Color
  colorMode: ColorMode;
  bgHue: number; // -1 = apagado, 0..360
  headHue: number;
  textHue: number;
  // Navegación
  srMode: boolean;
  keyboardNav: boolean;
  smartNav: boolean;
  tts: boolean;
  voice: boolean;
  vkb: boolean;
  dict: boolean;
  // Meta
  profile: Profile;
  lang: Lang;
  off: boolean;
}

export const defaultState: A11yState = {
  fontSize: 0,
  lineSp: 0,
  wordSp: 0,
  letterSp: 0,
  readableFont: false,
  links: false,
  imgDesc: false,
  blinks: false,
  muteMedia: false,
  magnifier: false,
  textMag: false,
  readFocus: false,
  readGuide: false,
  cursor: "none",
  colorMode: "none",
  bgHue: -1,
  headHue: -1,
  textHue: -1,
  srMode: false,
  keyboardNav: false,
  smartNav: false,
  tts: false,
  voice: false,
  vkb: false,
  dict: false,
  profile: "",
  lang: "es",
  off: false,
};

// Presets: qué activa cada perfil (se aplican SOBRE el estado por defecto).
export const PROFILES: Record<Exclude<Profile, "">, Partial<A11yState>> = {
  motor: { keyboardNav: true, cursor: "black", smartNav: true },
  blind: { srMode: true, tts: true, keyboardNav: true },
  colorblind: { colorMode: "highsat", links: true },
  dyslexia: { readableFont: true, lineSp: 2, wordSp: 2, letterSp: 1, readGuide: true },
  lowvision: { fontSize: 4, colorMode: "contrast", cursor: "black", textMag: true },
  cognitive: { readFocus: true, links: true, lineSp: 1 },
  epilepsy: { blinks: true, colorMode: "lowsat", muteMedia: true },
  adhd: { readFocus: true, muteMedia: true, blinks: true },
};

const CURSOR_WHITE = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='44' height='44' viewBox='0 0 24 24'%3E%3Cpath fill='%23fff' stroke='%23000' stroke-width='1.2' d='M4 2l16 8-7 1 4 8-3 1.5-4-8L4 18z'/%3E%3C/svg%3E") 4 2, auto`;
const CURSOR_BLACK = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='44' height='44' viewBox='0 0 24 24'%3E%3Cpath fill='%23000' stroke='%23fff' stroke-width='1.2' d='M4 2l16 8-7 1 4 8-3 1.5-4-8L4 18z'/%3E%3C/svg%3E") 4 2, auto`;

// Selectores que intentan NO romper fuentes de iconos al forzar tipografía.
const NOT_ICON =
  ':not([class*="icon"]):not([class*="fa-"]):not(.fa):not(.fas):not(.far):not(.material-icons):not(.material-symbols-outlined):not(svg):not(path)';

export const PAGE_CSS = `
/* ---- Filtros de color (sobre body para no romper el position:fixed del widget) ---- */
html[data-a11y-color="mono"] body { filter: grayscale(1) !important; }
html[data-a11y-color="lowsat"] body { filter: saturate(.45) !important; }
html[data-a11y-color="highsat"] body { filter: saturate(2.4) !important; }
html[data-a11y-color="contrast"] body { filter: contrast(1.5) !important; }

/* ---- Contraste alto oscuro: colores forzados (más fiable que invertir) ---- */
html[data-a11y-color="darkhc"] body,
html[data-a11y-color="darkhc"] body *:not(img):not(video):not(svg):not(iframe) {
  background-color: #0b0f14 !important;
  color: #ffffff !important;
  border-color: #8a94a6 !important;
  text-shadow: none !important;
  box-shadow: none !important;
}
html[data-a11y-color="darkhc"] a, html[data-a11y-color="darkhc"] a * { color: #ffd400 !important; text-decoration: underline !important; }
html[data-a11y-color="darkhc"] h1, html[data-a11y-color="darkhc"] h2, html[data-a11y-color="darkhc"] h3,
html[data-a11y-color="darkhc"] h4, html[data-a11y-color="darkhc"] h5, html[data-a11y-color="darkhc"] h6 { color: #4dd2ff !important; }
html[data-a11y-color="darkhc"] button, html[data-a11y-color="darkhc"] input,
html[data-a11y-color="darkhc"] select, html[data-a11y-color="darkhc"] textarea {
  background-color: #1a2230 !important; color: #fff !important; border: 2px solid #fff !important;
}

/* ---- Contraste alto claro ---- */
html[data-a11y-color="brighthc"] body,
html[data-a11y-color="brighthc"] body *:not(img):not(video):not(svg):not(iframe) {
  background-color: #ffffff !important;
  color: #000000 !important;
  border-color: #000 !important;
  text-shadow: none !important;
}
html[data-a11y-color="brighthc"] a, html[data-a11y-color="brighthc"] a * { color: #0000ee !important; text-decoration: underline !important; font-weight: 700 !important; }
html[data-a11y-color="brighthc"] button, html[data-a11y-color="brighthc"] input,
html[data-a11y-color="brighthc"] select, html[data-a11y-color="brighthc"] textarea {
  background-color: #fff !important; color: #000 !important; border: 2px solid #000 !important;
}

/* ---- Colores personalizados (matiz elegido por el visitante) ---- */
html[data-a11y-cbg] body,
html[data-a11y-cbg] body *:not(img):not(video):not(svg):not(iframe):not(button):not(input):not(select):not(textarea) {
  background-color: var(--a11y-bg) !important;
}
html[data-a11y-chead] h1, html[data-a11y-chead] h2, html[data-a11y-chead] h3,
html[data-a11y-chead] h4, html[data-a11y-chead] h5, html[data-a11y-chead] h6,
html[data-a11y-chead] h1 *, html[data-a11y-chead] h2 *, html[data-a11y-chead] h3 * {
  color: var(--a11y-head) !important;
}
html[data-a11y-ctext] body p, html[data-a11y-ctext] body li, html[data-a11y-ctext] body span,
html[data-a11y-ctext] body td, html[data-a11y-ctext] body label, html[data-a11y-ctext] body div {
  color: var(--a11y-text) !important;
}

/* ---- Espaciado (propiedades heredadas: funcionan en cualquier sitio) ---- */
html[data-a11y-linesp] body, html[data-a11y-linesp] body * { line-height: var(--a11y-linesp) !important; }
html[data-a11y-wordsp] body, html[data-a11y-wordsp] body * { word-spacing: var(--a11y-wordsp) !important; }
html[data-a11y-lettersp] body, html[data-a11y-lettersp] body * { letter-spacing: var(--a11y-lettersp) !important; }

/* ---- Fuente legible (evitando clases típicas de iconos) ---- */
html[data-a11y-readable] body *${NOT_ICON} {
  font-family: Verdana, Tahoma, "Trebuchet MS", Arial, sans-serif !important;
}

/* ---- Resaltar enlaces ---- */
html[data-a11y-links] a, html[data-a11y-links] a * {
  background-color: #fef3c7 !important; color: #78350f !important;
  text-decoration: underline !important;
}
html[data-a11y-links] a { outline: 2px solid #f59e0b !important; outline-offset: 1px; }

/* ---- Bloquear parpadeos / animaciones ---- */
html[data-a11y-blinks] *, html[data-a11y-blinks] *::before, html[data-a11y-blinks] *::after {
  animation: none !important;
  transition: none !important;
  scroll-behavior: auto !important;
}

/* ---- Cursor grande ---- */
html[data-a11y-cursor="white"], html[data-a11y-cursor="white"] * { cursor: ${CURSOR_WHITE} !important; }
html[data-a11y-cursor="black"], html[data-a11y-cursor="black"] * { cursor: ${CURSOR_BLACK} !important; }

/* ---- Foco visible reforzado (navegación por teclado) ---- */
html[data-a11y-kbd] *:focus, html[data-a11y-smart] *:focus {
  outline: 4px solid #e11d48 !important;
  outline-offset: 3px !important;
  border-radius: 2px;
}
`;

const LINE_LEVELS = ["", "1.5", "1.8", "2.1", "2.4"];
const WORD_LEVELS = ["", "0.12em", "0.24em", "0.36em", "0.5em"];
const LETTER_LEVELS = ["", "0.05em", "0.1em", "0.15em", "0.2em"];

// ---- Escalado de fuente por JS ----
// Cambiar font-size en <html> solo funciona en sitios hechos con rem/em. Para que
// funcione en CUALQUIER sitio (incluidos los que usan px), guardamos el tamaño
// original calculado de cada elemento con texto y aplicamos el factor en línea.
const origSize = new WeakMap<HTMLElement, number>();
let scaledEls: HTMLElement[] = [];

const TEXT_SELECTOR =
  "p,li,td,th,dd,dt,span,a,em,strong,small,label,button,input,select,textarea,h1,h2,h3,h4,h5,h6,blockquote,figcaption,caption,summary,legend,div";

function scaleFonts(level: number): void {
  const factor = 1 + level * 0.1;
  // Restaurar lo escalado previamente
  for (const el of scaledEls) el.style.removeProperty("font-size");
  scaledEls = [];
  if (level === 0) return;

  const host = document.getElementById("a11y-widget-root");
  const nodes = document.body.querySelectorAll<HTMLElement>(TEXT_SELECTOR);
  let count = 0;
  for (const el of Array.from(nodes)) {
    if (count > 6000) break; // tope de seguridad en páginas enormes
    if (host && host.contains(el)) continue;
    // Solo elementos con texto directo (no contenedores vacíos)
    let hasText = false;
    for (const child of Array.from(el.childNodes)) {
      if (child.nodeType === 3 && (child.textContent ?? "").trim()) {
        hasText = true;
        break;
      }
    }
    const isFormControl = /^(INPUT|SELECT|TEXTAREA|BUTTON)$/.test(el.tagName);
    if (!hasText && !isFormControl) continue;
    let base = origSize.get(el);
    if (base === undefined) {
      base = parseFloat(getComputedStyle(el).fontSize) || 16;
      origSize.set(el, base);
    }
    el.style.setProperty("font-size", (base * factor).toFixed(2) + "px", "important");
    scaledEls.push(el);
    count++;
  }
}

export function applyState(s: A11yState): void {
  const h = document.documentElement;
  const set = (attr: string, on: boolean, value = "") => {
    if (on && !s.off) h.setAttribute(attr, value);
    else h.removeAttribute(attr);
  };

  set("data-a11y-color", s.colorMode !== "none", s.colorMode);
  set("data-a11y-links", s.links);
  set("data-a11y-readable", s.readableFont);
  set("data-a11y-blinks", s.blinks);
  set("data-a11y-cursor", s.cursor !== "none", s.cursor);
  set("data-a11y-kbd", s.keyboardNav);
  set("data-a11y-smart", s.smartNav);

  set("data-a11y-linesp", s.lineSp > 0);
  set("data-a11y-wordsp", s.wordSp > 0);
  set("data-a11y-lettersp", s.letterSp > 0);
  if (s.lineSp > 0) h.style.setProperty("--a11y-linesp", LINE_LEVELS[s.lineSp]);
  if (s.wordSp > 0) h.style.setProperty("--a11y-wordsp", WORD_LEVELS[s.wordSp]);
  if (s.letterSp > 0) h.style.setProperty("--a11y-lettersp", LETTER_LEVELS[s.letterSp]);

  set("data-a11y-cbg", s.bgHue >= 0);
  set("data-a11y-chead", s.headHue >= 0);
  set("data-a11y-ctext", s.textHue >= 0);
  if (s.bgHue >= 0) h.style.setProperty("--a11y-bg", `hsl(${s.bgHue},60%,92%)`);
  if (s.headHue >= 0) h.style.setProperty("--a11y-head", `hsl(${s.headHue},85%,32%)`);
  if (s.textHue >= 0) h.style.setProperty("--a11y-text", `hsl(${s.textHue},70%,22%)`);

  try {
    scaleFonts(s.off ? 0 : s.fontSize);
  } catch {
    /* nunca romper la página del cliente */
  }
}
